'use client';

import { Log, UserManager, UserManagerSettings } from 'oidc-client-ts';
import React, { useEffect, useRef } from 'react';
import { AuthProvider as OIDCAuthProvider } from 'react-oidc-context';

type OAuthProviderContextType = {
  manager: UserManager | null;
};

const OAuthProviderContext = React.createContext<OAuthProviderContextType>(
  {
    manager: null,
  },
);

export const OAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const redirectURI = typeof window !== 'undefined' ? `${window.location.origin}/callback` : '';
  const postLogoutURI = typeof window !== 'undefined' ? `${window.location.origin}/logout` : '';
  const settings: UserManagerSettings = {
    authority: process.env.AUTH_CLIENT_ISSUER || `https://${process.env.AUTH_CLIENT_DOMAIN}`,
    client_id: process.env.AUTH_CLIENT_ID as string,
    redirect_uri: redirectURI,
    post_logout_redirect_uri: postLogoutURI,
    scope: process.env.AUTH_CLIENT_SCOPE,
    loadUserInfo: true,
    automaticSilentRenew: true,
    response_type: 'code',
    extraQueryParams: { audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER as string },
  };
  const { current: manager } = useRef(new UserManager(settings));

  const context: OAuthProviderContextType = React.useMemo(() => ({
    manager: manager,
  }), []);

  useEffect(() => {
    Log.setLogger(console);
    Log.setLevel(Log.WARN);
  }, []);

  return (
    <OIDCAuthProvider userManager={manager} skipSigninCallback>
      <OAuthProviderContext.Provider value={context}>{children}</OAuthProviderContext.Provider>
    </OIDCAuthProvider>
  );
};

export const useOAuthProvider = (): OAuthProviderContextType => React.useContext(OAuthProviderContext);
